'use client';

import { BigNum, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { MarketId, USDT_SPOT_MARKET_INDEX } from '@velocity-exchange/common';
import { useCallback } from 'react';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';
import { getLiveOracleData } from 'src/stores/marketLiveState';

const USDT_SPOT_MARKET_ID = MarketId.createSpotMarket(USDT_SPOT_MARKET_INDEX);

/**
 * Reads the best live oracle price for a market from the market live-state
 * module (best across transports — pricing-ws, DLOB, rpc). The rpc adapter now
 * publishes oracle data for every client-subscribed market, so the module is
 * the single source; there is no longer a direct velocity-client pull to merge.
 * Falls back to $1 for USDC/USDT and zero otherwise when nothing has published.
 */
const useGetOraclePriceForMarket = () => {
	return useCallback((marketId: MarketId | undefined): BigNum => {
		if (!marketId) return ZERO_PRICE_BIGNUM;

		const oracleData = getLiveOracleData(marketId);
		if (oracleData) return oracleData.price;

		return marketId.equals(USDT_SPOT_MARKET_ID)
			? BigNum.fromPrint('1', PRICE_PRECISION_EXP)
			: ZERO_PRICE_BIGNUM;
	}, []);
};

export default useGetOraclePriceForMarket;
